import { isHealthCheckRequest } from '../../core-layer/utils/health-check.utils'
import { BaseService } from '../../http-layer/service/base.service'

import type { LocationData } from '../types/location'

interface LegacyPortalCustomCodeShape {
  customJs?: string | null
  customCss?: string | null
  headerCode?: string | null
  footerCode?: string | null
}

function normalizePortalCustomCode(data: LocationData): LocationData {
  if (data.customCode) return data

  const legacy = data as LocationData & LegacyPortalCustomCodeShape
  const hasLegacyCustomCode =
    Boolean(legacy.customJs) || Boolean(legacy.customCss) || Boolean(legacy.headerCode) || Boolean(legacy.footerCode)

  if (!hasLegacyCustomCode) return data

  return {
    ...data,
    customCode: {
      customJs: legacy.customJs ?? undefined,
      customCss: legacy.customCss ?? undefined,
      headerCode: legacy.headerCode ?? undefined,
      footerCode: legacy.footerCode ?? undefined,
      // Legacy portal-settings shape has flat custom code fields without liveMode.
      // Treat presence of any legacy custom code as enabled to preserve old behavior.
      liveMode: true,
    },
  }
}

export class LocationService extends BaseService {
  constructor() {
    super({
      baseURL: 'CLIENTPORTAL_SERVER_URL',
      basePath: '',
    })
  }

  /**
   * Fetch portal settings by domain
   * @param domain - Portal domain to resolve
   * @param options - Optional browser cache mode for the request (e.g. `no-cache` to always revalidate)
   */
  async fetchPortalSettings(domain: string, options?: { cache?: RequestCache }): Promise<LocationData> {
    try {
      const endpoint = `/portal-settings?domain=${encodeURIComponent(domain)}`

      const { data } = await this.get<LocationData>(endpoint, options)
      const normalizedData = normalizePortalCustomCode(data)

      return normalizedData
    } catch (error) {
      console.error('❌ [LocationService.fetchPortalSettings] Error fetching portal settings:', error)
      throw error
    }
  }

  /**
   * Get the domain to use for fetching settings
   */
  getDomainForSettings(host?: string): string | undefined {
    // If host is provided, use it
    if (host) {
      // Skip API calls for health check requests (IP addresses, etc.)
      if (isHealthCheckRequest(host)) {
        console.debug('[LocationService] Health check detected, skipping API call for host:', host)
        return undefined // Signal to skip API call
      }

      // For localhost, use the staging domain
      if (host.includes('localhost')) {
        return 'communities.staging.clientclub.net'
      }
      return host
    }

    // Client-side: use window.location
    if (typeof window !== 'undefined') {
      // For localhost, use the staging domain
      if (window.location.hostname === 'localhost') {
        return 'communities.staging.clientclub.net'
      }

      return window.location.hostname
    }

    // Server-side fallback: When no host is available (internal requests, health checks, etc.)
    // Return null to signal that this is likely an internal/health check request
    console.debug('[LocationService] No host available on server-side, treating as internal request')
    return undefined
  }

  /**
   * Check if we're in a PWA environment
   */
  isPwa(): boolean {
    if (typeof window === 'undefined') return false

    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as unknown as { standalone?: boolean }).standalone ||
      document.referrer.includes('android-app://') ||
      window.location.href.includes('mode=pwa')
    )
  }

  /**
   * Get session location ID from storage if available
   */
  getSessionLocationId(): string | null {
    if (typeof window === 'undefined') return null

    try {
      // Check various storage locations
      const sessionStorage = window.sessionStorage.getItem('locationId')
      if (sessionStorage) return sessionStorage

      const localStorage = window.localStorage.getItem('locationId')
      if (localStorage) return localStorage

      // Check cookies
      const cookies = document.cookie.split(';')
      const locationCookie = cookies.find(c => c.trim().startsWith('locationId='))
      if (locationCookie) {
        return locationCookie.split('=')[1]
      }
    } catch (error) {
      console.error('Error getting session location ID:', error)
    }

    return null
  }
}
